import type { ReactNode } from 'react'
import { useMainButton, useNotificationOnce, type MainButtonConfig, type NotificationType } from '@/shared/telegram'
import { LinkButton, ResultState, type IconName, type ResultIconTone, type ResultTone } from '@/shared/ui'

export type StateViewProps = {
  /** Иконка набора в круге 84px. */
  icon: IconName
  /**
   * Круг: `neutral` (`tg-segment-bg`) — отказы, ошибки, отмена; `blue` —
   * успех; `red` — отказ, отмена кассиром. Отмена — всегда `neutral`, она не ошибка.
   */
  tone?: ResultTone
  /** Цвет иконки в нейтральном круге: `hint` или `destructive` (платёж не прошёл, наш сбой). */
  iconTone?: ResultIconTone
  title: string
  text: ReactNode
  /** Технический код моно-шрифтом: для скриншота в поддержку, выделяемый. */
  code?: string | null
  mainButton?: MainButtonConfig | null
  /** Текстовая кнопка под текстом: второстепенное действие. */
  secondary?: { label: string; onClick: () => void; isDisabled?: boolean } | null
  /**
   * Сообщение о неудавшемся действии на этом же экране (повтор оплаты без
   * сети): экран остаётся, а под кодом появляется строка, что пошло не так.
   */
  notice?: string | null
  /** Отклик один раз при показе — по карте Haptic. */
  haptic?: NotificationType | null
}

/**
 * Единый шаблон отказных экранов (CLAUDE.md, «Ошибки и пустые состояния»)
 * поверх `ResultState` кита: круг 84px с иконкой 38px → заголовок 20 →
 * пояснение `.lead` → технический код моно → MainButton → текстовая кнопка.
 * Одинаковый визуальный язык отказов важнее индивидуальности каждого — копии
 * шаблона в экранах не заводятся.
 *
 * Колонка `max-w-app` — своя, а не только от оболочки: отказы подписи,
 * блокировки и ошибки маршрута рисуются вне `AppLayout`.
 */
export const StateView = ({
  icon,
  tone = 'neutral',
  iconTone = 'hint',
  title,
  text,
  code,
  mainButton = null,
  secondary = null,
  notice = null,
  haptic = null,
}: StateViewProps) => {
  useMainButton(mainButton)
  useNotificationOnce(haptic)

  return (
    <div className="mx-auto flex w-full max-w-app flex-1 flex-col">
      <ResultState tone={tone} icon={icon} iconTone={iconTone} title={title} body={text}>
        {code && (
          <code className="mt-1 max-w-67.5 font-mono text-meta break-all text-tg-hint select-all">{code}</code>
        )}

        {notice && (
          <p role="alert" className="m-0 max-w-67.5 text-meta text-pretty text-tg-destructive">
            {notice}
          </p>
        )}

        {secondary && (
          <LinkButton className="mt-1" onClick={secondary.onClick} isDisabled={secondary.isDisabled}>
            {secondary.label}
          </LinkButton>
        )}
      </ResultState>
    </div>
  )
}
